import * as React from "react";
import { MutableRefObject, useEffect, useMemo, useRef } from "react";
import { OSM, Vector as VectorSource } from "ol/source";
import { Tile as TileLayer, Vector as VectorLayer } from "ol/layer";
import { Feature, View, Map } from "ol";
import { defaults as defaultControls } from "ol/control";
import { getMiniSymbol } from "./miniSymbols";
import { OnMiniFeatureClickCallback } from "./miniMapProvider";
import { config } from "../config";
import { IDestinationMiniMap } from "./destinationMiniMapProvider";

const center = config.defaults.center;

let onMiniFeatureClick: MutableRefObject<OnMiniFeatureClickCallback>;

export const useDestinationMiniMap = (target: MutableRefObject<HTMLDivElement>, props: IDestinationMiniMap): Map => {
    const _miniMap = useRef<Map>(null);

    onMiniFeatureClick = useRef<OnMiniFeatureClickCallback>(props.onClickOnMiniFeature);
    onMiniFeatureClick.current = useMemo(() => props.onClickOnMiniFeature, [props.onClickOnMiniFeature]);

    useEffect(() => {
        if (!props.destinationId || !target.current || !props.destinationList || !props.destinationList.length) {
            return;
        }

        const destinationFeature = props.destinationList.find((f) => f.get("OBJECTID") === props.destinationId).clone();
        if (!destinationFeature) {
            return;
        }

        const routeLayer = new VectorLayer({
            source: new VectorSource({ features: [destinationFeature] }),
            style: (f) => getMiniSymbol(f.get("PLACESUBTYPE"), f.get("VISITED"), 0, f.get("SELECTED"))
        });

        const osmLayer = new TileLayer({ source: new OSM() });

        _miniMap.current = new Map({
            layers: [osmLayer, routeLayer],
            target: target.current,
            view: new View({
                projection: "EPSG:3857",
                center,
                zoom: 4
            }),
            controls: defaultControls({ zoom: false, rotate: false, attribution: false })
        });

        _miniMap.current.on(
            "click",
            (evt) => !evt.dragging && _miniMap.current.forEachFeatureAtPixel(evt.pixel, handleObjectClick)
        );

        const handleObjectClick = (feature: Feature) =>
            onMiniFeatureClick.current?.({ layerId: "clusterLayer", objectId: feature.get("OBJECTID") });

        zoomToFeature(destinationFeature);
    }, [props.destinationId]);

    const zoomToFeature = (feature: Feature, callback?: CallableFunction): void => {
        const geometry = feature.getGeometry();
        _miniMap.current.getView().fit(geometry, { maxZoom: config.defaults.zoomToLevel, callback });
    };
};
